// Config panel for the "internal_event" trigger block
// Lets user pick which internal system event this automation listens for
import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';

// Pre-defined event types that the backend fires
const KNOWN_EVENTS = [
  {
    value: 'alarm.created',
    label: 'Alarm Created',
    description: 'Fires when a new emergency alarm is created',
    variables: [
      'event.alarmId', 'event.alarmUUID', 'event.alarmType', 'event.alarmState',
      'event.locationId', 'event.locationName', 'event.personId', 'event.personName',
      'event.deviceKey', 'event.deviceName', 'event.buttonType', 'event.confidence'
    ]
  },
  {
    value: 'alarm.status_changed',
    label: 'Alarm Status Changed',
    description: 'Fires when an alarm transitions to a new state',
    variables: [
      'event.alarmId', 'event.alarmUUID', 'event.previousState', 'event.newState',
      'event.newStateName', 'event.isTerminal', 'event.locationId',
      'event.personName', 'event.comment'
    ]
  },
  {
    value: 'alarm.*',
    label: 'Any Alarm Event',
    description: 'Fires on any alarm-related event (created, status changed, etc.)',
    variables: ['event.alarmId', 'event.alarmUUID', 'event.locationId', 'eventType']
  },
];

const InternalEventConfig = ({ config, onChange, onSummaryChange }) => {
  const handleChange = (field, value) => {
    const updated = { ...config, [field]: value };
    onChange(updated);

    const known = KNOWN_EVENTS.find(e => e.value === updated.eventType);
    onSummaryChange(known ? known.label : (updated.eventType || 'Internal Event'));
  };

  const selectedEvent = KNOWN_EVENTS.find(e => e.value === config.eventType);
  const isCustom = config.eventType && !selectedEvent;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Triggers this automation when an internal system event occurs.
        Events are fired by backend processes (alarm creation, status changes, etc.).
      </Typography>

      <FormControl size="small" fullWidth>
        <InputLabel>Event Type</InputLabel>
        <Select
          value={KNOWN_EVENTS.some(e => e.value === config.eventType) ? config.eventType : '_custom'}
          onChange={(e) => {
            if (e.target.value === '_custom') {
              handleChange('eventType', '');
            } else {
              handleChange('eventType', e.target.value);
            }
          }}
          label="Event Type"
        >
          {KNOWN_EVENTS.map(evt => (
            <MenuItem key={evt.value} value={evt.value}>
              <Box>
                <Typography variant="body2">{evt.label}</Typography>
                <Typography variant="caption" color="text.secondary">{evt.description}</Typography>
              </Box>
            </MenuItem>
          ))}
          <MenuItem value="_custom">
            <Typography variant="body2">Custom Event Type...</Typography>
          </MenuItem>
        </Select>
      </FormControl>

      {(isCustom || (!selectedEvent && config.eventType !== undefined)) && (
        <TextField
          size="small"
          label="Custom Event Type"
          value={config.eventType || ''}
          onChange={(e) => handleChange('eventType', e.target.value.replace(/[^a-zA-Z0-9_.*-]/g, ''))}
          placeholder="e.g. device.offline"
          helperText="Use dot notation for namespacing. Use .* suffix for wildcards (e.g. alarm.*)"
        />
      )}

      {selectedEvent && (
        <>
          <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
            <Typography variant="caption">
              <strong>{selectedEvent.label}</strong><br />
              {selectedEvent.description}
            </Typography>
          </Alert>

          <Box>
            <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
              Available Variables
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Use these in downstream blocks via {'{{variableName}}'} syntax:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedEvent.variables.map(v => (
                <Chip
                  key={v}
                  label={`{{${v}}}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.6rem', fontFamily: 'monospace' }}
                />
              ))}
            </Box>
          </Box>
        </>
      )}

      <Alert severity="success" variant="outlined" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          <strong>How it works:</strong><br />
          When the selected event fires in the backend, this automation will execute automatically.
          The event data is available as variables (prefixed with <code style={{ fontSize: '0.6rem' }}>event.</code>)
          in all downstream blocks.
        </Typography>
      </Alert>
    </Box>
  );
};

export default InternalEventConfig;
